import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, Shield, Calendar, DollarSign, Edit, Trash2 } from 'lucide-react';
import { assetService } from '../../../services/assetService';
import { formatCurrency } from '../../../utils/currencyFormatter';

export default function AssetDetailsModal({ isOpen, onClose, assetId, userCurrency, onEdit, onDelete }) {
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && assetId) {
      loadAssetDetails();
    }
  }, [isOpen, assetId]);

  const loadAssetDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await assetService?.getById(assetId);
      setAsset(data);
    } catch (err) {
      setError(err?.message || 'Failed to load asset details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    onEdit?.(asset);
    onClose?.();
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetService?.delete(assetId);
        onDelete?.();
        onClose?.();
      } catch (err) {
        setError(err?.message || 'Failed to delete asset');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Asset Details</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading asset details...</p>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        ) : asset ? (
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Asset Name</p>
                  <p className="text-base font-medium text-gray-900">{asset?.assetName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Asset Type</p>
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                    {asset?.assetType}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Value</p>
                  <p className="text-base font-medium text-gray-900 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    {formatCurrency(asset?.assetValue, userCurrency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Assigned Staff Member</p>
                  <p className="text-base font-medium text-gray-900">{asset?.assignedStaffMember}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Location</p>
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full flex items-center gap-1 w-fit ${
                    asset?.location === 'in_office' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    <MapPin className="w-4 h-4" />
                    {asset?.location === 'in_office' ? 'In Office' : 'Remote'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Insurance Status</p>
                  <span className={`px-3 py-1 text-sm font-semibold rounded-full flex items-center gap-1 w-fit ${
                    asset?.isInsured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Shield className="w-4 h-4" />
                    {asset?.isInsured ? 'Insured' : 'Not Insured'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates and Warranty */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dates & Warranty</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Purchase Date
                  </p>
                  <p className="text-base font-medium text-gray-900">
                    {asset?.purchaseDate ? new Date(asset.purchaseDate)?.toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Warranty Expiry
                  </p>
                  <p className="text-base font-medium text-gray-900">
                    {asset?.warrantyExpiry ? new Date(asset.warrantyExpiry)?.toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Serial Number</p>
                  <p className="text-base font-medium text-gray-900">{asset?.serialNumber || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Specifications */}
            {asset?.specifications && Object.keys(asset?.specifications)?.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(asset?.specifications)?.map(([key, value]) => (
                    <div key={key} className="bg-white rounded-lg p-3">
                      <p className="text-sm text-gray-600 mb-1">{key}</p>
                      <p className="text-base font-medium text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {asset?.notes && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{asset?.notes}</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}